import React, {useContext, useEffect, useRef, useState} from 'react';
import block from 'bem-cn-lite';
import {AppContext} from 'app/context';
import {Navigate, useNavigate} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {
    Alert,
    Icon,
    Link,
    Modal,
    Text,
    TextInput,
    useToaster,
    Button as GButton,
    Button
} from '@gravity-ui/uikit';
import {CApi} from '@services/api';
import {Eye, EyeSlash, Xmark} from '@gravity-ui/icons';
import {refreshBasketStorage} from 'app/shouldRefreshBasketStorage';
import {useMediaQuery} from 'react-responsive';
import {ToastVisibilityContext} from '@app/Providers/CustomToasterProvider/ToastVisibilityContext';

import './Login.scss';

const b = block('login-page');

interface LoginForm {
    login: string;
    password: string;
}

export const Login: React.FC = () => {
    const isSmallScreen = useMediaQuery({query: '(max-width: 1150px)'});
    const appCtx = useContext(AppContext);
    const inputRef = useRef<HTMLInputElement>(null);
    const navigateTo = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const {
        register,
        handleSubmit,
        formState: {errors},
        getValues,
        setError
    } = useForm<LoginForm>({defaultValues: {login: '', password: ''}});

    const [showToast, setShowToast] = useState(false);
    const {add, remove} = useToaster();
    const {setCurrentToastName, currentToastName} = useContext(ToastVisibilityContext);
    const toastNameRef = useRef<string | null>(null);
    const toastName = 'login_attempt';

    const onSubmit = () => {
        appCtx.emit('increment', {name: 'login_attempt'});

        setLoading(true);
        CApi()
            .login.loginCreate({
                ...getValues(),
                ...{type: 'password'}
            })
            .then((r) => {
                setLoading(false);
                appCtx.emit('clear_cnt', {name: 'login_attempt'});
                appCtx.emit('login', {token: r.data.access_token});
                refreshBasketStorage.set(true);
            })
            .catch(() => {
                if (appCtx.state.counters['login_attempt'] >= 5) {
                    setShowToast(true);
                }
                setLoading(false);
                setError('login', {message: 'Неправильный логин или пароль'});
                setError('password', {message: 'Неправильный логин или пароль'});
            });
    };

    useEffect(() => {
        if (inputRef.current) {
            const input = inputRef.current;
            // setTimeout ensures this runs after the browser's default focus behavior
            setTimeout(() => {
                input.setSelectionRange(input.value.length, input.value.length);
            }, 0);
        }
    }, [showPassword]);

    useEffect(() => {
        if (showToast && appCtx.state.counters['login_attempt'] >= 5) {
            setCurrentToastName(toastName);
            toastNameRef.current = toastName;

            add({
                name: toastName,
                title: 'Вы забыли пароль?',
                theme: 'info',
                content: (
                    <div className={b('toast-content')}>
                        <Text variant="body-2">
                            Вы более 5 раз ввели неверные данные. Можно восстановить пароль.
                        </Text>
                        <GButton
                            size="xl"
                            onClick={() => navigateTo('/remind-pass')}
                            view="normal-contrast"
                        >
                            Да, восстановить пароль
                        </GButton>
                    </div>
                ),
                autoHiding: false,
                isClosable: true
            });
        }
    }, [showToast]);

    const updateToasterStyles = () => {
        const root = document.documentElement;

        if (currentToastName === toastName) {
            root.style.setProperty('--g-toaster-top', isSmallScreen ? '100px' : 'auto');
            root.style.setProperty('--g-toaster-left', isSmallScreen ? '50%' : 'auto');
            root.style.setProperty('--g-toaster-right', isSmallScreen ? 'auto' : '34px');
            root.style.setProperty(
                '--g-toaster-transform',
                isSmallScreen ? 'translate(-50%, -50%)' : 'none'
            );
            root.style.setProperty('--g-toaster-inset-block-end', isSmallScreen ? 'auto' : '0');
            root.style.setProperty('--g-toaster-visibility', 'visible');
            root.style.setProperty('--g-toaster-width', isSmallScreen ? '328px' : '400px');
            root.style.setProperty('--g-toaster-content-width', isSmallScreen ? '232px' : '304px');
        }
    };

    useEffect(() => {
        updateToasterStyles();
    }, [currentToastName]);

    // Удаляем тост при размонтировании/смене страницы
    useEffect(() => {
        const root = document.documentElement;

        return () => {
            if (toastNameRef.current) {
                root.style.setProperty('--g-toaster-visibility', 'hidden');
                root.style.setProperty('--g-toaster-width', '400px');
                root.style.setProperty('--g-toaster-content-width', '304px');
                root.style.setProperty('--g-toaster-left', '50%');
                root.style.setProperty('--g-toaster-right', 'auto');
                root.style.setProperty('--g-toaster-transform', 'translate(-50%, -50%)');
                root.style.setProperty('--g-toaster-inset-block-end', 'auto');
                remove(toastNameRef.current);
                toastNameRef.current = null;
            }
        };
    }, [remove]);

    useEffect(() => {
        if (isSmallScreen) {
            const handleEscape = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    navigateTo('/');
                }
            };

            const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollBarWidth}px`;

            document.addEventListener('keydown', handleEscape);

            return () => {
                document.removeEventListener('keydown', handleEscape);
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
            };
        }

        return undefined;
    }, [isSmallScreen, navigateTo]);

    if (appCtx.state.token) {
        refreshBasketStorage.set(true);
        return <Navigate to="/home" />;
    }

    const formContent = (
        <>
            <div className={b('close-button')}>
                <GButton onClick={() => navigateTo('/')} view="flat" size="m">
                    <Icon data={Xmark} size={16} />
                </GButton>
            </div>
            <Text variant="header-2">Вход в Личный кабинет</Text>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className={b('input')}>
                    <Text variant="body-2">Логин</Text>
                    <TextInput
                        size="xl"
                        {...register('login', {
                            required: 'Обязательно'
                        })}
                        errorMessage={errors.login && errors.login.message}
                        errorPlacement="outside"
                        validationState={errors.login && 'invalid'}
                    />
                </div>
                <div className={b('input')}>
                    <Text variant="body-2">Пароль</Text>
                    <div className={b('password-container')}>
                        <TextInput
                            type={showPassword ? 'text' : 'password'}
                            size="xl"
                            {...register('password', {
                                required: 'Обязательно'
                            })}
                            errorMessage={errors.password && errors.password.message}
                            errorPlacement="outside"
                            validationState={errors.password && 'invalid'}
                            controlRef={inputRef}
                        ></TextInput>
                        <div
                            className={b('password-icon-container')}
                            onClick={() => {
                                setShowPassword((prev) => !prev);
                            }}
                        >
                            {showPassword ? (
                                <EyeSlash className={b('password-icon')} />
                            ) : (
                                <Eye className={b('password-icon')} />
                            )}
                        </div>
                    </div>
                </div>
                <div className={b('input')}>
                    <Button view="action" type="submit" size="xl" loading={loading}>
                        Войти
                    </Button>
                </div>
                <div className={b('input')}>
                    <Link view="normal" href="#" onClick={() => navigateTo('/remind-pass')}>
                        <Text variant="body-2">Забыли пароль?</Text>
                    </Link>
                </div>
                <div className={b('input')}>
                    <Alert
                        theme="info"
                        title="Регистрация аккаунта"
                        message={
                            <>
                                Свяжитесь с нашими менеджерами: <br />
                                +7(383)375-16-62 или avtoem@zapnsk.ru
                            </>
                        }
                    />
                </div>
            </form>
        </>
    );

    if (isSmallScreen) {
        return (
            <div className={`${b()} ${b()}--fullscreen`}>
                <div className={b('fullscreen-container')}>
                    <div className={b('content')}>{formContent}</div>
                </div>
            </div>
        );
    }

    return (
        <Modal open keepMounted className={b()}>
            {formContent}
        </Modal>
    );
};
